import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { X } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { openViaHandoff } from "@/components/AdminBar"
import { PageHeader } from "@/components/PageHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"
import { StatusChip } from "@/components/ui/status-chip"
import { CategoryTag } from "@/components/ui/category-tag"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { PageProps } from "@/types/inertia"

type Article = {
  brand?: string
  preview_path: string
  id: number
  title_en: string
  category: string
  author: string
  status: string
  published_at: string | null
  created_at: string
}

type Props = PageProps & {
  articles: Article[]
  categories: string[]
  filters: { search?: string; status?: string; category?: string }
}

export default function ArticlesIndex() {
  const { props } = usePage<Props>()
  const { articles, categories, filters } = props
  const flash = props.flash

  const [search, setSearch] = React.useState(filters.search ?? "")
  const [status, setStatus] = React.useState(filters.status ?? "")
  const [category, setCategory] = React.useState(filters.category ?? "")
  const [removeId, setRemoveId] = React.useState<number | null>(null)
  const [removeTitle, setRemoveTitle] = React.useState("")

  // Filtering happens here rather than on the server, the way the public blog
  // index does it: the whole list (for this brand) is already in props, so a
  // keystroke narrows the table with no round trip and clearing a filter
  // brings the rows straight back. The server still reads the same query
  // params on the way in, so a filtered view can be linked to.
  const filtered = React.useMemo(() => {
    const words = search.trim().toLowerCase()
    return articles.filter((a) => {
      if (status && a.status !== status) return false
      if (category && a.category !== category) return false
      if (!words) return true
      return `${a.title_en} ${a.category ?? ""} ${a.author ?? ""}`.toLowerCase().includes(words)
    })
  }, [articles, search, status, category])

  const filtering = !!(search || status || category)

  // Keep the address bar in step, so a filtered view survives a reload and can
  // be shared or bookmarked. The brand scope belongs to the server and is left
  // exactly as it arrived.
  React.useEffect(() => {
    const url = new URL(window.location.href)
    const set = (key: string, value: string) =>
      value ? url.searchParams.set(key, value) : url.searchParams.delete(key)
    set("search", search)
    set("status", status)
    set("category", category)
    window.history.replaceState(window.history.state, "", url)
  }, [search, status, category])

  function clearFilters() {
    setSearch("")
    setStatus("")
    setCategory("")
  }

  function handleRemove() {
    if (!removeId) return
    router.delete(`/admin/articles/${removeId}`)
    setRemoveId(null)
  }

  return (
    <AdminShell>
      <Head title="Blog">
        <meta name="description" content="Manage blog articles and resources for Sanctuary Therapy Centre." />
        <meta property="og:title" content="Blog" />
        <meta property="og:description" content="Manage blog articles and resources for Sanctuary Therapy Centre." />
      </Head>

      <PageHeader
        title="Blog"
        description="Manage blog articles and resources."
        actions={
          <Button asChild>
            <Link href="/admin/articles/new">+ New Article</Link>
          </Button>
        }
      />

      {flash?.notice && (
        <p className="mt-4 text-sm text-accent">{flash.notice}</p>
      )}
      {flash?.alert && (
        <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>
      )}

      {/* Filters — live, so there is no button to press. */}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Input
          type="search"
          placeholder="Search title, category or author…"
          aria-label="Search articles"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
        />
        <Select
          aria-label="Filter by status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="w-40"
        >
          <option value="">All statuses</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
        </Select>
        <Select
          aria-label="Filter by category"
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className="w-44"
        >
          <option value="">All categories</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </Select>
        {/* Offered only when there is something to clear — a button for the
            state you are already in is one more thing to read. */}
        {filtering && (
          <Button type="button" variant="link" className="gap-1 px-1" onClick={clearFilters}>
            <X className="h-4 w-4" aria-hidden />
            Clear filters
          </Button>
        )}
        <p className="ml-auto text-sm text-ink-muted" aria-live="polite">
          {filtering
            ? `${filtered.length} of ${articles.length} article${articles.length === 1 ? "" : "s"}`
            : `${articles.length} article${articles.length === 1 ? "" : "s"}`}
        </p>
      </div>

      {/* Table */}
      <div className="mt-6 overflow-hidden rounded-lg border border-hairline">
        <table className="w-full text-sm">
          <thead className="border-b border-hairline bg-surface">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Title</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted sm:table-cell">Category</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted md:table-cell">Author</th>
              <th className="px-4 py-3 text-left font-medium text-ink-muted">Status</th>
              <th className="hidden px-4 py-3 text-left font-medium text-ink-muted lg:table-cell">Published</th>
              <th className="px-4 py-3 text-right font-medium text-ink-muted">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-ink-muted">
                  {filtering ? "No articles match these filters." : "No articles yet."}
                </td>
              </tr>
            )}
            {filtered.map((a) => (
              <tr key={a.id} className="bg-white hover:bg-surface/30 transition-colors">
                <td className="px-4 py-3">
                  <p className="font-medium text-ink-display">{a.title_en}</p>
                </td>
                <td className="hidden px-4 py-3 sm:table-cell">
                  {a.category ? (
                    <CategoryTag label={a.category} />
                  ) : (
                    <span className="text-ink-muted">—</span>
                  )}
                </td>
                <td className="hidden px-4 py-3 text-ink-body md:table-cell">
                  {a.author || <span className="text-ink-muted">—</span>}
                </td>
                <td className="px-4 py-3">
                  <StatusChip status={a.status as "published" | "draft"} />
                </td>
                <td className="hidden px-4 py-3 text-ink-muted lg:table-cell">
                  {a.published_at
                    ? new Date(a.published_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })
                    : "—"}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <Button asChild variant="secondary" size="sm">
                      <Link href={`/admin/articles/${a.id}/edit`}>Edit</Link>
                    </Button>
                    {/* The public page on the article's own brand, via the
                        login handoff so a draft opens signed in. */}
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => openViaHandoff(a.brand ?? "gisela", a.preview_path)}
                    >
                      {a.status === "published" ? "View" : "Preview"}
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="text-danger-display hover:border-danger hover:bg-danger-faded"
                      onClick={() => { setRemoveId(a.id); setRemoveTitle(a.title_en) }}
                    >
                      Remove
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Remove confirmation */}
      <Dialog open={removeId !== null} onOpenChange={(open) => !open && setRemoveId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove article?</DialogTitle>
            <DialogDescription>
              This will permanently remove <strong>{removeTitle}</strong> and cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setRemoveId(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRemove}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminShell>
  )
}
